import {throwError as observableThrowError, Observable, combineLatest} from 'rxjs';

import {catchError, map, switchMap, take} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpHeaders, HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {LanguageService} from '../../language/language.service';
import {SupportedLocaleConfigurationService} from '../uiconfiguration/supportedlocaleconfiguration.service';
import {IDMRequest} from './request';
import {FilterResult} from '../../resource/filterresult';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';

@Injectable()
export class RequestService {
  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private locService: SupportedLocaleConfigurationService,
    private layoutService: LayoutService,
    private envConfig: EnvironmentConfigService,
  ) {}

  public getRequests(userid: string): Observable<IDMRequest[]> {
    let headers = new HttpHeaders({'Content-Type': 'application/json'});

    return combineLatest([
      this.languageService.getLanguage(this.locService.getSupportedLocaleConfiguration()),
      this.layoutService.endpoint$.pipe(take(1)),
    ]).pipe(
      switchMap(([language, env]: [string, Endpoint]) => {
        const params = this.buildParams(env, userid, language);

        return this.http
          .get(env.getUrl() + 'v2/resources/', {
            headers: headers,
            params: params,
            ...(!env.isCloud() ? {withCredentials: true} : {}),
          })
          .pipe(
            map((response: FilterResult) => this.toRequests(response.Results)),
            catchError(RequestService.handleError),
          );
      }),
    );
  }

  /**
   * The Service keeps requests as an audit trail without workflow states: the caller's requests,
   * newest first. MIM: the caller's finished requests of the last day plus every request still
   * in flight, localized.
   */
  public buildParams(env: Endpoint, userid: string, language: string): HttpParams {
    const attributes = [
      this.envConfig.getAttributeName('ObjectID'),
      this.envConfig.getAttributeName('DisplayName'),
      this.envConfig.getAttributeName('RequestStatus'),
      this.envConfig.getAttributeName('CreatedTime'),
    ]
      .filter(attr => attr !== null)
      .join(',');

    if (env.isCloud()) {
      return new HttpParams()
        .append(
          'filter',
          '/' +
            this.envConfig.getObjectTypeName('Request') +
            '[' +
            this.envConfig.getAttributeName('Creator') +
            " = '" +
            userid +
            "']",
        )
        .append('attributes', attributes)
        .append('pageSize', '50')
        .append('sortAttribute', this.envConfig.getAttributeName('CreatedTime'))
        .append('sortDirection', 'desc');
    }
    return new HttpParams()
      .append(
        'filter',
        "/Request[((RequestStatus = 'Completed' or RequestStatus = 'Committed' or RequestStatus = 'NotFound' or RequestStatus = 'Canceled' or RequestStatus = 'Canceling' or RequestStatus = 'CanceledPostProcessing' or RequestStatus = 'Failed' or RequestStatus = 'PostProcessingError' or RequestStatus = 'Denied') and Creator='" +
          userid +
          "' and (CreatedTime>op:subtract-dayTimeDuration-from-dateTime(fn:current-dateTime(), xs:dayTimeDuration('P1D')))) or ((RequestStatus = 'PostProcessing' or RequestStatus = 'Validating' or RequestStatus = 'Authorized' or RequestStatus = 'Authorizing' or RequestStatus = 'Authenticated' or RequestStatus = 'Authenticating') and Creator='" +
          userid +
          "')]",
      )
      .append('attributes', attributes)
      .append('locale', language);
  }

  private static handleError(error: HttpErrorResponse) {
    console.error(error);
    return observableThrowError(error || 'Server error');
  }

  private toRequest(r: any): IDMRequest {
    let request = <IDMRequest>{
      id: r[this.envConfig.getAttributeName('ObjectID')],
      displayname: r[this.envConfig.getAttributeName('DisplayName')],
      status: r[this.envConfig.getAttributeName('RequestStatus')],
      createdtime: r[this.envConfig.getAttributeName('CreatedTime')],
    };
    return request;
  }

  private toRequests(resources: any): IDMRequest[] {
    const finalResources: IDMRequest[] = [];
    resources.forEach(obj => {
      finalResources.push(this.toRequest(obj));
    });
    return finalResources;
  }
}
